'use client';

import { createAppKit } from '@reown/appkit/react';
import { mainnet } from '@reown/appkit/networks';
import React, { createContext, type ReactNode, useMemo } from 'react';
import { cookieToInitialState, WagmiProvider } from 'wagmi';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import type { Config } from '@wagmi/core';

export const AppKitContext = createContext<{
  wagmiConfig?: Config;
}>({});

export function AppKit({
  children,
  cookies,
  projectId,
  projectName,
  projectDescription,
  projectUrl,
}: {
  children: ReactNode;
  cookies: string | null;
  projectId: string;
  projectName: string;
  projectDescription: string;
  projectUrl: string;
}) {
  const wagmiAdapter = useMemo(() => {
    return new WagmiAdapter({
      ssr: true,
      projectId,
      networks: [mainnet],
    });
  }, [projectId]);
  const metadata = useMemo(() => {
    return {
      name: projectName,
      description: projectDescription,
      url: projectUrl,
      icons: ['https://assets.reown.com/reown-profile-pic.png'],
    };
  }, [projectName, projectDescription, projectUrl]);

  const initialState = cookieToInitialState(wagmiAdapter.wagmiConfig, cookies);
  createAppKit({
    adapters: [wagmiAdapter],
    projectId,
    networks: [mainnet],
    defaultNetwork: mainnet,
    metadata: metadata,
    features: {
      socials: false,
      email: false,
      history: false,
      swaps: false,
      analytics: false,
    },
  });

  return (
    <AppKitContext.Provider
      value={{
        wagmiConfig: wagmiAdapter.wagmiConfig,
      }}
    >
      <WagmiProvider
        config={wagmiAdapter.wagmiConfig}
        initialState={initialState}
      >
        {children}
      </WagmiProvider>
    </AppKitContext.Provider>
  );
}
